import { type Dispatch, type SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ActivePageContext } from '@trinity/chat-components/components/Chat/context';
import { SidePanelToggleContext } from '@trinity/chat-components/components/Chat.SidePanel/context';
import { readSidePanelOpenState, writeSidePanelOpenState } from './side-panel-open-state';
import { SidePanelExpandedContext } from './context';

type Props = {
  children: React.ReactNode;
};

export const SidePanelContainer = (props: Props) => {
  const { layout, pageId } = useContext(ActivePageContext);
  const { isOpen } = useContext(SidePanelToggleContext);

  // Whether a host app currently offers the panel at all: Sentiment drives this through
  // SidePanelToggleContext, GenAI through a `side_chat` page layout.
  const canShow = layout === 'side_chat' || isOpen;

  // User dismissal is tracked separately so the close button can hide a panel that
  // `canShow` would otherwise force open — GenAI mounts no SidePanelToggleContext
  // provider, so `isOpen` never changes there and `closePanel()` is a no-op.
  const [dismissed, setDismissed] = useState(() => readSidePanelOpenState() === 'closed');

  // Reset the dismissal whenever the panel becomes available again, so every existing
  // open path still works: `openPanel()` in Sentiment, or navigating to another
  // `side_chat` page in GenAI. Keyed on the false -> true transition (or a change of
  // `pageId` while already showing, so navigating between `side_chat` pages resets a
  // prior dismissal) rather than on `canShow` alone, otherwise a re-render while open
  // would undo an active dismissal. Runs in an effect (rather than during render) so
  // state updates stay outside render. read/write side panel open state from session storage
  // overrides the default open/closed state to persist user's most recent sidepanel toggle.
  const previousCanShowRef = useRef(canShow);
  const previousPageIdRef = useRef(pageId);
  useEffect(() => {
    const previousCanShow = previousCanShowRef.current;
    const previousPageId = previousPageIdRef.current;
    previousCanShowRef.current = canShow;
    previousPageIdRef.current = pageId;
    if (canShow && (!previousCanShow || pageId !== previousPageId)) {
      setDismissed(readSidePanelOpenState() === 'closed');
    }
  }, [canShow, pageId]);

  const isExpanded = canShow && !dismissed;

  const setIsExpanded = useCallback<Dispatch<SetStateAction<boolean>>>(
    (value) => {
      setDismissed((previousDismissed) => {
        const currentExpanded = canShow && !previousDismissed;
        const nextExpanded = typeof value === 'function' ? value(currentExpanded) : value;
        writeSidePanelOpenState(nextExpanded ? 'open' : 'closed');
        return !nextExpanded;
      });
    },
    [canShow]
  );

  const expandedValue = useMemo<[boolean, Dispatch<SetStateAction<boolean>>]>(
    () => [isExpanded, setIsExpanded],
    [isExpanded, setIsExpanded]
  );

  return (
    <SidePanelExpandedContext.Provider value={expandedValue}>
      {props.children}
    </SidePanelExpandedContext.Provider>
  );
};
